/**
 * 自定义编解码工具
 * 使用简单的混淆算法，避免 userId 被直接识别和篡改
 */

// 混淆密钥（可以定期更换）
const SECRET_KEY = 'sm2024';

/**
 * 编码 userId
 * @param {number|string} userId - 用户ID
 * @returns {string} 编码后的字符串
 */
export function encodeUserId(userId) {
  const str = String(userId);
  
  // 简单的 XOR 混淆 + Base64
  let result = '';
  for (let i = 0; i < str.length; i++) {
    result += String.fromCharCode(
      str.charCodeAt(i) ^ SECRET_KEY.charCodeAt(i % SECRET_KEY.length)
    );
  }
  
  // Base64 编码并替换特殊字符，使其 URL 安全
  return btoa(result)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/**
 * 解码 userId
 * @param {string} encoded - 编码后的字符串
 * @returns {number|null} 解码后的用户ID，失败返回 null
 */
export function decodeUserId(encoded) {
  try {
    // 还原 Base64
    let base64 = encoded
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    
    // 补齐 Base64 填充
    while (base64.length % 4) {
      base64 += '=';
    }
    
    const decoded = atob(base64);
    
    // XOR 解密
    let result = '';
    for (let i = 0; i < decoded.length; i++) {
      result += String.fromCharCode(
        decoded.charCodeAt(i) ^ SECRET_KEY.charCodeAt(i % SECRET_KEY.length)
      );
    }
    
    // 解析 userId
    const userId = parseInt(result, 10);
    if (!isNaN(userId) && userId > 0) {
      return userId;
    }
    return null;
  } catch (error) {
    console.error('解码失败:', error);
    return null;
  }
}

/**
 * 异步压缩并编码数据（用于 Dify URL）
 * Dify 要求：GZIP 压缩 + Base64 编码 + URL 编码
 * @param {any} data - 要编码的数据
 * @returns {Promise<string>} 编码后的字符串
 */
export async function compressAndEncode(data) {
  // A. 数据转字符串，统一加上 userId: 前缀
  const strData = 'userId:' + String(data);

  // B. 转二进制 (Uint8Array)
  const encoder = new TextEncoder();
  const inputBuffer = encoder.encode(strData);

  // C. Gzip 压缩 (使用浏览器原生 API)
  const gzipStream = new CompressionStream('gzip');
  const writer = gzipStream.writable.getWriter();
  writer.write(inputBuffer);
  writer.close();

  const compressedBuffer = await new Response(gzipStream.readable).arrayBuffer();
  const compressedBytes = new Uint8Array(compressedBuffer);

  // D. 转 Base64 (使用 Uint8Array 方式避免空字节问题)
  let base64String = arrayBufferToBase64(compressedBytes);
  
  // E. 转换为 Base64URL 格式：去掉 = 填充，替换 + 为 -，/ 为 _
  base64String = base64String
    .replace(/=/g, '')
    .replace(/\+/g, '-')
    .replace(/\//g, '_');
  
  return base64String;
}

/**
 * 将 Uint8Array 转换为 Base64 字符串
 * 使用这种方式避免二进制字符串中的空字节问题
 * @param {Uint8Array} bytes - 字节数组
 * @returns {string} Base64 字符串
 */
function arrayBufferToBase64(bytes) {
  const base64Chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  let result = '';
  let i = 0;

  while (i < bytes.length) {
    // 取三个字节
    const byte1 = bytes[i++];
    const byte2 = i < bytes.length ? bytes[i++] : null;
    const byte3 = i < bytes.length ? bytes[i++] : null;

    // 编码为 4 个 Base64 字符
    const index1 = byte1 >> 2;
    const index2 = ((byte1 & 0x03) << 4) | (byte2 !== null ? byte2 >> 4 : 0);
    const index3 = byte2 !== null ? ((byte2 & 0x0F) << 2) | (byte3 !== null ? byte3 >> 6 : 0) : null;
    const index4 = byte3 !== null ? byte3 & 0x3F : null;

    result += base64Chars[index1];
    result += base64Chars[index2];
    result += index3 !== null ? base64Chars[index3] : '=';
    result += index4 !== null ? base64Chars[index4] : '=';
  }

  return result;
}
